import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Badge, Tooltip, OverlayTrigger, InputGroup } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import 'bootstrap/dist/css/bootstrap.min.css';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalogoPermisos, setCatalogoPermisos] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('success');
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  
  // Estados para filtros
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroCorreo, setFiltroCorreo] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  
  // Estados para modal de cambio de rol
  const [showModalRol, setShowModalRol] = useState(false);
  const [usuarioEditandoRol, setUsuarioEditandoRol] = useState(null);
  const [nuevoRoleId, setNuevoRoleId] = useState('');
  const [guardandoRol, setGuardandoRol] = useState(false);

  // Estados para modal de permisos personalizados
  const [showModalPermisos, setShowModalPermisos] = useState(false);
  const [usuarioEditandoPermisos, setUsuarioEditandoPermisos] = useState(null);
  const [permisosOverride, setPermisosOverride] = useState({});
  const [usarPermisosPersonalizados, setUsarPermisosPersonalizados] = useState(false);
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);

  // Estados para modal de datos básicos
  const [showModalDatos, setShowModalDatos] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [datosEditando, setDatosEditando] = useState({ nombres: '', correo: '', tipoDocumento: 'CC', numeroDocumento: '' });
  const [guardandoDatos, setGuardandoDatos] = useState(false);

  // Estados para firma manual (subir imagen)
  const [showModalFirma, setShowModalFirma] = useState(false);
  const [usuarioFirma, setUsuarioFirma] = useState(null);
  const [firmaPreview, setFirmaPreview] = useState(null);
  const [subiendoFirma, setSubiendoFirma] = useState(false);
  const fileInputRef = useRef(null);
  const [showModalVerFirma, setShowModalVerFirma] = useState(false);
  const [firmaViendo, setFirmaViendo] = useState(null);

  // ⭐ NUEVO: Estado para solicitar firma al funcionario
  const [solicitandoFirma, setSolicitandoFirma] = useState(false);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastTipo, setToastTipo] = useState('success');

  const correoUsuario = localStorage.getItem('correo');

  // =====================================================
  // CARGAR DATOS INICIALES
  // =====================================================
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchCatalogoPermisos();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      mostrarToast('Error al cargar usuarios', 'error');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/config/roles`, {
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        }
      });
      const data = await response.json();
      if (data.success) {
        setRoles(data.data.filter(r => r.activo !== false));
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
    }
  };

  const fetchCatalogoPermisos = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/config/permisos`, {
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        }
      });
      const data = await response.json();
      if (data.success) {
        setCatalogoPermisos(data.data);
      }
    } catch (error) {
      console.error('Error al cargar catálogo de permisos:', error);
    }
  };

  // =====================================================
  // TOAST
  // =====================================================
  const mostrarToast = (texto, tipo = 'success', duracion = 3000) => {
    setMensaje(texto);
    setTipoMensaje(tipo);
    setShowToast(true);
    if (tipo !== 'loading') {
      setTimeout(() => setShowToast(false), duracion);
    }
  };

  const ocultarToast = () => setShowToast(false);

  // =====================================================
  // FUNCIONES DE USUARIO
  // =====================================================
  const handleDesactivar = async (id, nombre) => {
    if (!window.confirm(`¿Desactivar el usuario ${nombre}?`)) return;
    mostrarToast('Desactivando usuario...', 'loading');
    try {
      await fetch(`${API_BASE}/api/delete/${id}`, {
        method: 'DELETE',
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      mostrarToast(`Usuario ${nombre} desactivado`, 'success');
      fetchUsers();
    } catch (error) {
      mostrarToast('Error al desactivar usuario', 'error');
    }
  };

  const handleReactivar = async (id, nombre) => {
    if (!window.confirm(`¿Reactivar el usuario ${nombre}?`)) return;
    mostrarToast('Reactivando usuario...', 'loading');
    try {
      await fetch(`${API_BASE}/api/reactivate-user/${id}`, {
        method: 'PUT',
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      mostrarToast(`Usuario ${nombre} reactivado`, 'success');
      fetchUsers();
    } catch (error) {
      mostrarToast('Error al reactivar usuario', 'error');
    }
  };

  const handleDesbloquear = async (userId, nombre) => {
    if (!window.confirm(`¿Desbloquear el usuario ${nombre}?`)) return;
    mostrarToast('Desbloqueando usuario...', 'loading');
    try {
      await fetch(`${API_BASE}/api/desbloquear-usuario/${userId}`, {
        method: 'PUT',
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      mostrarToast(`Usuario ${nombre} desbloqueado`, 'success');
      fetchUsers();
    } catch (error) {
      mostrarToast('Error al desbloquear usuario', 'error');
    }
  };

  const handleEliminarPermanente = async (id, nombre) => {
    if (!window.confirm(`⚠️ ELIMINAR PERMANENTEMENTE a ${nombre}?\n\nEsta acción NO se puede deshacer.`)) return;
    mostrarToast('Eliminando usuario...', 'loading');
    try {
      await fetch(`${API_BASE}/api/delete-permanently/${id}`, {
        method: 'DELETE',
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      mostrarToast(`Usuario ${nombre} eliminado permanentemente`, 'success');
      fetchUsers();
    } catch (error) {
      mostrarToast('Error al eliminar usuario', 'error');
    }
  };

  // =====================================================
  // MODAL: EDITAR DATOS BÁSICOS
  // =====================================================
  const handleAbrirModalDatos = (usuario) => {
    setUsuarioEditando(usuario);
    setDatosEditando({
      nombres: usuario.nombres,
      correo: usuario.correo,
      tipoDocumento: usuario.tipoDocumento || 'CC',
      numeroDocumento: usuario.numeroDocumento || ''
    });
    setShowModalDatos(true);
  };

  const handleGuardarDatos = async () => {
    if (!usuarioEditando) return;
    if (!datosEditando.nombres.trim() || !datosEditando.correo.trim()) {
      mostrarToast('Nombre y correo son obligatorios', 'error');
      return;
    }
    setGuardandoDatos(true);
    try {
      const response = await fetch(`${API_BASE}/api/update/${usuarioEditando._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        },
        body: JSON.stringify({
          nombres: datosEditando.nombres.trim(),
          correo: datosEditando.correo.trim().toLowerCase(),
          tipoDocumento: datosEditando.tipoDocumento,
          numeroDocumento: datosEditando.numeroDocumento.trim()
        })
      });
      if (response.ok) {
        mostrarToast('Datos actualizados correctamente', 'success');
        setShowModalDatos(false);
        fetchUsers();
      } else {
        const data = await response.json();
        mostrarToast(data.mensaje || 'Error al actualizar', 'error');
      }
    } catch (error) {
      mostrarToast('Error al actualizar datos', 'error');
    } finally {
      setGuardandoDatos(false);
    }
  };

  // =====================================================
  // MODAL: CAMBIAR ROL
  // =====================================================
  const handleAbrirModalRol = (usuario) => {
    setUsuarioEditandoRol(usuario);
    setNuevoRoleId(usuario.roleId || '');
    setShowModalRol(true);
  };

  const handleGuardarRol = async () => {
    if (!usuarioEditandoRol || !nuevoRoleId) return;
    setGuardandoRol(true);
    try {
      const rolNuevo = roles.find(r => r._id === nuevoRoleId);
      const response = await fetch(`${API_BASE}/api/update/${usuarioEditandoRol._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        },
        body: JSON.stringify({
          roleId: nuevoRoleId,
          rol: rolNuevo?.codigo || 'administrativo',
          permisos: rolNuevo?.permisos || {},       
          permisosOverride: {}                       
        })
      });
      if (response.ok) {
        mostrarToast(`Rol cambiado a "${rolNuevo?.nombre}"`, 'success');
        setShowModalRol(false);
        fetchUsers();
      } else {
        mostrarToast('Error al cambiar rol', 'error');
      }
    } catch (error) {
      mostrarToast('Error al cambiar rol', 'error');
    } finally {
      setGuardandoRol(false);
    }
  };

  // =====================================================
  // MODAL: PERMISOS PERSONALIZADOS
  // =====================================================
  const handleAbrirModalPermisos = (usuario) => {
    setUsuarioEditandoPermisos(usuario);
    const tieneOverride = usuario.permisosOverride && Object.keys(usuario.permisosOverride).length > 0;
    setUsarPermisosPersonalizados(tieneOverride);
    if (tieneOverride) {
      setPermisosOverride(usuario.permisosOverride);
    } else {
      const rolUsuario = roles.find(r => r._id === usuario.roleId || r.codigo === usuario.rol);
      setPermisosOverride(rolUsuario?.permisos || usuario.permisos || {});
    }
    setShowModalPermisos(true);
  };

  const handlePermisoChange = (permiso, valor) => {
    setPermisosOverride(prev => ({ ...prev, [permiso]: valor }));
  };

  const handleGuardarPermisos = async () => {
    if (!usuarioEditandoPermisos) return;
    setGuardandoPermisos(true);
    try {
      const dataToSend = {
        permisosOverride: usarPermisosPersonalizados ? permisosOverride : {},
        permisos: usarPermisosPersonalizados ? permisosOverride : undefined
      };
      const response = await fetch(`${API_BASE}/api/update/${usuarioEditandoPermisos._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        },
        body: JSON.stringify(dataToSend)
      });
      if (response.ok) {
        mostrarToast(
          usarPermisosPersonalizados 
            ? 'Permisos personalizados guardados' 
            : 'Permisos restablecidos al rol',
          'success'
        );
        setShowModalPermisos(false);
        fetchUsers();
      } else {
        mostrarToast('Error al guardar permisos', 'error');
      }
    } catch (error) {
      mostrarToast('Error al guardar permisos', 'error');
    } finally {
      setGuardandoPermisos(false);
    }
  };

  // =====================================================
  // FIRMA MANUAL (subir imagen)
  // =====================================================
  const handleAbrirModalFirma = (usuario) => {
    setUsuarioFirma(usuario);
    setFirmaPreview(null);
    setShowModalFirma(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      mostrarToast('Solo se permiten imágenes', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      mostrarToast('Máximo 2MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setFirmaPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubirFirma = async () => {
    if (!firmaPreview) return;
    setSubiendoFirma(true);
    try {
      const response = await fetch(`${API_BASE}/api/subir-firma/${usuarioFirma._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '' 
        },
        body: JSON.stringify({ firmaBase64: firmaPreview })
      });
      if (response.ok) {
        mostrarToast('Firma guardada', 'success');
        setShowModalFirma(false);
        fetchUsers();
      }
    } catch (error) {
      mostrarToast('Error al guardar firma', 'error');
    } finally {
      setSubiendoFirma(false);
    }
  };

  const handleVerFirma = async (usuario) => {
    try {
      mostrarToast('Cargando firma...', 'loading');
      const response = await fetch(`${API_BASE}/api/obtener-firma/${usuario._id}`, {
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      const data = await response.json();
      setFirmaViendo({ nombres: usuario.nombres, firma: data.firma });
      setShowModalVerFirma(true);
      ocultarToast();
    } catch (error) {
      mostrarToast('Error al cargar firma', 'error');
    }
  };

  const handleEliminarFirma = async (usuario) => {
    if (!window.confirm(`¿Eliminar la firma de ${usuario.nombres}?`)) return;
    try {
      await fetch(`${API_BASE}/api/eliminar-firma/${usuario._id}`, {
        method: 'DELETE',
        headers: { 'usuario-correo': correoUsuario || '' }
      });
      mostrarToast('Firma eliminada', 'success');
      fetchUsers();
    } catch (error) {
      mostrarToast('Error al eliminar firma', 'error');
    }
  };

  // =====================================================
  // ⭐ NUEVO: SOLICITAR FIRMA AL FUNCIONARIO
  // =====================================================
  const handleSolicitarFirma = async (usuario) => {
    if (!window.confirm(`¿Enviar solicitud de firma digital a ${usuario.nombres} (${usuario.correo})?`)) return;
    setSolicitandoFirma(usuario._id);
    mostrarToast('Enviando solicitud...', 'loading');
    try {
      const response = await fetch(`${API_BASE}/api/firma-funcionario/solicitar/${usuario._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || '',
          'usuario-rol': localStorage.getItem('rol') || '',
          'rol': localStorage.getItem('rol') || ''
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        mostrarToast(`✅ Solicitud enviada a ${data.correo}`, 'success');
      } else {
        mostrarToast(data.message || 'Error al enviar solicitud', 'error');
      }
    } catch (error) {
      mostrarToast('Error de conexión', 'error');
    } finally {
      setSolicitandoFirma(false);
    }
  };

  // =====================================================
  // HELPERS
  // =====================================================
  const getEstadoBadge = (usuario) => {
    if (!usuario.activo) return <Badge bg="secondary">Inactivo</Badge>;
    if (usuario.bloqueado) return <Badge bg="danger">Bloqueado</Badge>;
    if (usuario.intentosFallidos > 0) return <Badge bg="warning">⚠️ {usuario.intentosFallidos}</Badge>;
    return <Badge bg="success">Activo</Badge>;
  };

  const getRolInfo = (usuario) => {
    return roles.find(r => r._id === usuario.roleId) || roles.find(r => r.codigo === usuario.rol);
  };

  const tienePermisosPersonalizados = (usuario) => {
    return usuario.permisosOverride && Object.keys(usuario.permisosOverride).length > 0;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Nunca';
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return 'Error';
    }
  };

  const limpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroCorreo('');
    setFiltroRol('');
  };

  const usuariosFiltrados = users.filter(user => {
    if (!mostrarInactivos && !user.activo) return false;
    if (filtroNombre && !user.nombres.toLowerCase().includes(filtroNombre.toLowerCase())) return false;
    if (filtroCorreo && !user.correo.toLowerCase().includes(filtroCorreo.toLowerCase())) return false;
    if (filtroRol && user.rol !== filtroRol) return false;
    return true;
  });

  const esSuperAdmin = localStorage.getItem('rol') === 'superadmin';

  const nombresPermisos = {
    verDocumentosMedicos: 'Ver Docs Médicos',
    verDocumentosAdministrativos: 'Ver Docs Admin',
    enviarDocumentosMedicos: 'Enviar Docs Médicos',
    enviarDocumentosAdministrativos: 'Enviar Docs Admin',
    verReportes: 'Ver Reportes',
    firmarDocumentos: 'Firmar Documentos',
    anularDocumentos: 'Anular Documentos',
    gestionarUsuarios: 'Gestionar Usuarios',
    gestionarPlantillas: 'Gestionar Plantillas',
    configurarSistema: 'Configurar Sistema'
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <>
      <style>{`
        .toast-container { position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 350px; }
        .toast-notification { background: white; border-radius: 10px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); padding: 20px; border-left: 5px solid; animation: slideInRight 0.3s ease-out; position: relative; }
        .toast-notification.success { border-left-color: #28a745; }
        .toast-notification.error { border-left-color: #dc3545; }
        .toast-notification.loading { border-left-color: #007bff; }
        .toast-header { display: flex; align-items: center; margin-bottom: 8px; font-weight: bold; }
        .toast-icon { font-size: 20px; margin-right: 10px; }
        .toast-icon.success { color: #28a745; }
        .toast-icon.error { color: #dc3545; }
        .toast-icon.loading { color: #007bff; animation: spin 1s linear infinite; }
        .toast-close { position: absolute; top: 10px; right: 15px; background: none; border: none; font-size: 18px; cursor: pointer; }
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        
        .rol-badge { padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
        .rol-color { width: 8px; height: 8px; border-radius: 50%; }
        
        .permission-toggle { display: flex; align-items: center; justify-content: space-between; padding: 10px 15px; background: #f8f9fa; border-radius: 8px; margin-bottom: 8px; }
        .permission-toggle:hover { background: #e9ecef; }
        
        .form-check-input:checked { background-color: #28a745; border-color: #28a745; }
      `}</style>

      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />

        <div className="flex-grow-1 p-4" style={{ marginLeft: sidebarAbierto ? '240px' : '80px', transition: 'margin-left 0.3s', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
          <div className="container-fluid">
            
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">
                  <i className="bi bi-people-fill me-2"></i>
                  Administración de Usuarios
                </h2>
                <p className="text-muted mb-0">Gestión de roles, permisos y estados</p>
              </div>
              <div>
                <Badge bg="info" className="me-2">{users.filter(u => u.activo).length} Activos</Badge>
                <Badge bg="secondary" className="me-2">{users.filter(u => !u.activo).length} Inactivos</Badge>
                <Badge bg="warning">{users.filter(u => u.bloqueado).length} Bloqueados</Badge>
              </div>
            </div>

            {/* FILTROS */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">🔍 Nombre</label>
                    <InputGroup>
                      <Form.Control placeholder="Buscar..." value={filtroNombre} onChange={(e) => setFiltroNombre(e.target.value)} />
                      {filtroNombre && <Button variant="outline-secondary" onClick={() => setFiltroNombre('')}>✕</Button>}
                    </InputGroup>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">📧 Correo</label>
                    <InputGroup>
                      <Form.Control placeholder="Buscar..." value={filtroCorreo} onChange={(e) => setFiltroCorreo(e.target.value)} />
                      {filtroCorreo && <Button variant="outline-secondary" onClick={() => setFiltroCorreo('')}>✕</Button>}
                    </InputGroup>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">👤 Rol</label>
                    <Form.Select value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)}>
                      <option value="">Todos los roles</option>
                      {roles.map(rol => (
                        <option key={rol._id} value={rol.codigo}>{rol.nombre}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-3">
                    <Button variant="outline-secondary" className="w-100" onClick={limpiarFiltros}>🗑️ Limpiar</Button>
                  </div>
                </div>
                <div className="mt-3 d-flex align-items-center">
                  <Form.Check type="switch" id="toggleInactivos" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.target.checked)} label="Incluir inactivos" className="me-4" />
                  <span className="text-muted">Mostrando <strong>{usuariosFiltrados.length}</strong> de <strong>{users.length}</strong></span>
                </div>
              </div>
            </div>

            {/* TABLA */}
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th className="text-center">Firma</th>
                        <th>Último Acceso</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosFiltrados.map((user) => {
                        const rolInfo = getRolInfo(user);
                        return (
                          <tr key={user._id} className={!user.activo ? 'table-secondary' : ''}>
                            <td>
                              <div className="fw-bold">{user.nombres}</div>
                              <small className="text-muted d-block">{user.correo}</small>
                              {user.tipoDocumento && user.numeroDocumento && (
                                <small className="text-muted">{user.tipoDocumento}: {user.numeroDocumento}</small>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span 
                                  className="rol-badge" 
                                  style={{ 
                                    backgroundColor: `${rolInfo?.color || '#6b7280'}20`,
                                    color: rolInfo?.color || '#6b7280'
                                  }}
                                >
                                  <span className="rol-color" style={{ backgroundColor: rolInfo?.color || '#6b7280' }}></span>
                                  {rolInfo?.nombre || user.rol}
                                </span>
                                {tienePermisosPersonalizados(user) && (
                                  <OverlayTrigger placement="top" overlay={<Tooltip>Tiene permisos personalizados</Tooltip>}>
                                    <Badge bg="info" style={{ cursor: 'help' }}>✨</Badge>
                                  </OverlayTrigger>
                                )}
                              </div>
                            </td>
                            <td>{getEstadoBadge(user)}</td>

                            {/* ═══════════════════════════════════════════════ */}
                            {/* COLUMNA FIRMA — con botón solicitar firma       */}
                            {/* ═══════════════════════════════════════════════ */}
                            <td className="text-center">
                              {user.tieneFirma ? (
                                <div className="d-flex justify-content-center gap-1 flex-wrap">
                                  <Button size="sm" variant="outline-success" onClick={() => handleVerFirma(user)} title="Ver firma">👁️</Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => handleEliminarFirma(user)} title="Eliminar firma">🗑️</Button>
                                  {esSuperAdmin && (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Solicitar nueva firma al funcionario</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => handleSolicitarFirma(user)}
                                        title="Solicitar nueva firma"
                                        disabled={solicitandoFirma === user._id}
                                      >
                                        {solicitandoFirma === user._id ? '⏳' : '🔄'}
                                      </Button>
                                    </OverlayTrigger>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex justify-content-center gap-1 flex-wrap">
                                  <OverlayTrigger placement="top" overlay={<Tooltip>Cargar firma manualmente</Tooltip>}>
                                    <Button size="sm" variant="outline-warning" onClick={() => handleAbrirModalFirma(user)} title="Cargar firma manual">
                                      ⚠️ Cargar
                                    </Button>
                                  </OverlayTrigger>
                                  {esSuperAdmin && (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Enviar solicitud de firma por correo</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => handleSolicitarFirma(user)}
                                        title="Solicitar firma al funcionario"
                                        disabled={solicitandoFirma === user._id}
                                      >
                                        {solicitandoFirma === user._id ? '⏳' : '📧 Solicitar'}
                                      </Button>
                                    </OverlayTrigger>
                                  )}
                                </div>
                              )}
                            </td>

                            <td><small className="text-muted">{formatearFecha(user.ultimoAcceso)}</small></td>
                            <td className="text-center">
                              <div className="btn-group">
                                <Button size="sm" variant="outline-success" onClick={() => handleAbrirModalDatos(user)} title="Editar datos">✏️</Button>
                                <Button size="sm" variant="outline-primary" onClick={() => handleAbrirModalRol(user)} title="Cambiar rol">👤</Button>
                                <Button size="sm" variant="outline-info" onClick={() => handleAbrirModalPermisos(user)} title="Permisos">🔐</Button>
                                {user.bloqueado && (
                                  <Button size="sm" variant="outline-warning" onClick={() => handleDesbloquear(user._id, user.nombres)} title="Desbloquear">🔓</Button>
                                )}
                                {user.activo ? (
                                  <Button size="sm" variant="outline-secondary" onClick={() => handleDesactivar(user._id, user.nombres)} title="Desactivar">⏸️</Button>
                                ) : (
                                  <>
                                    <Button size="sm" variant="outline-success" onClick={() => handleReactivar(user._id, user.nombres)} title="Reactivar">▶️</Button>
                                    {esSuperAdmin && (
                                      <Button size="sm" variant="outline-danger" onClick={() => handleEliminarPermanente(user._id, user.nombres)} title="Eliminar">🗑️</Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODAL: EDITAR DATOS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal show={showModalDatos} onHide={() => setShowModalDatos(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>✏️ Editar Usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Nombre Completo</Form.Label>
            <Form.Control value={datosEditando.nombres} onChange={(e) => setDatosEditando({...datosEditando, nombres: e.target.value})} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Correo</Form.Label>
            <Form.Control type="email" value={datosEditando.correo} onChange={(e) => setDatosEditando({...datosEditando, correo: e.target.value})} />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Tipo Doc</Form.Label>
                <Form.Select value={datosEditando.tipoDocumento} onChange={(e) => setDatosEditando({...datosEditando, tipoDocumento: e.target.value})}>
                  <option value="CC">CC</option>
                  <option value="TI">TI</option>
                  <option value="CE">CE</option>
                  <option value="Pasaporte">Pasaporte</option>
                  <option value="PEP">PEP</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Número</Form.Label>
                <Form.Control value={datosEditando.numeroDocumento} onChange={(e) => setDatosEditando({...datosEditando, numeroDocumento: e.target.value.replace(/[^0-9]/g, '')})} maxLength="15" />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalDatos(false)}>Cancelar</Button>
          <Button variant="success" onClick={handleGuardarDatos} disabled={guardandoDatos}>
            {guardandoDatos ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> : '💾 Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODAL: CAMBIAR ROL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal show={showModalRol} onHide={() => setShowModalRol(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>👤 Cambiar Rol - {usuarioEditandoRol?.nombres}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            Al cambiar el rol, los permisos del usuario se actualizarán automáticamente según el nuevo rol.
          </Alert>
          <Form.Group>
            <Form.Label className="fw-bold">Seleccionar Rol</Form.Label>
            {roles.map(rol => (
              <div 
                key={rol._id}
                className={`p-3 mb-2 rounded border ${nuevoRoleId === rol._id ? 'border-primary bg-light' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setNuevoRoleId(rol._id)}
              >
                <div className="d-flex align-items-center">
                  <Form.Check 
                    type="radio" 
                    name="rol" 
                    checked={nuevoRoleId === rol._id}
                    onChange={() => setNuevoRoleId(rol._id)}
                    className="me-3"
                  />
                  <span className="rol-color me-2" style={{ backgroundColor: rol.color || '#6b7280', width: '12px', height: '12px', borderRadius: '50%', display: 'inline-block' }}></span>
                  <div>
                    <strong>{rol.nombre}</strong>
                    {rol.esInmutable && <Badge bg="warning" className="ms-2">🔒 Protegido</Badge>}
                    <br />
                    <small className="text-muted">{rol.descripcion || 'Sin descripción'}</small>
                  </div>
                </div>
              </div>
            ))}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalRol(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleGuardarRol} disabled={guardandoRol || !nuevoRoleId}>
            {guardandoRol ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> : '💾 Guardar Rol'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODAL: PERMISOS PERSONALIZADOS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal show={showModalPermisos} onHide={() => setShowModalPermisos(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>🔐 Permisos - {usuarioEditandoPermisos?.nombres}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant={usarPermisosPersonalizados ? 'warning' : 'info'}>
            <Form.Check 
              type="switch"
              id="toggle-permisos-personalizados"
              label={usarPermisosPersonalizados ? '✨ Usando permisos PERSONALIZADOS' : '👤 Usando permisos del ROL'}
              checked={usarPermisosPersonalizados}
              onChange={(e) => {
                setUsarPermisosPersonalizados(e.target.checked);
                if (!e.target.checked) {
                  const rolUsuario = roles.find(r => r._id === usuarioEditandoPermisos?.roleId || r.codigo === usuarioEditandoPermisos?.rol);
                  setPermisosOverride(rolUsuario?.permisos || {});
                }
              }}
              className="fw-bold"
            />
            <small className="d-block mt-2">
              {usarPermisosPersonalizados 
                ? 'Los permisos personalizados sobrescriben los del rol. El usuario mantendrá estos permisos aunque cambie de rol.'
                : 'El usuario hereda los permisos de su rol. Si el rol cambia, los permisos se actualizan automáticamente.'}
            </small>
          </Alert>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-primary mb-3">📋 Visualización</h6>
              {['verDocumentosMedicos', 'verDocumentosAdministrativos', 'verReportes'].map(permiso => (
                <div key={permiso} className="permission-toggle">
                  <span>{nombresPermisos[permiso]}</span>
                  <Form.Check 
                    type="switch"
                    checked={permisosOverride[permiso] || false}
                    onChange={() => handlePermisoChange(permiso, !permisosOverride[permiso])}
                    disabled={!usarPermisosPersonalizados}
                  />
                </div>
              ))}
            </div>
            <div className="col-md-6">
              <h6 className="text-success mb-3">✏️ Acciones</h6>
              {['enviarDocumentosMedicos', 'enviarDocumentosAdministrativos', 'firmarDocumentos', 'anularDocumentos'].map(permiso => (
                <div key={permiso} className="permission-toggle">
                  <span>{nombresPermisos[permiso]}</span>
                  <Form.Check 
                    type="switch"
                    checked={permisosOverride[permiso] || false}
                    onChange={() => handlePermisoChange(permiso, !permisosOverride[permiso])}
                    disabled={!usarPermisosPersonalizados}
                  />
                </div>
              ))}
              <h6 className="text-warning mb-3 mt-4">⚙️ Administración</h6>
              {['gestionarUsuarios', 'gestionarPlantillas', 'configurarSistema'].map(permiso => (
                <div key={permiso} className="permission-toggle">
                  <span>{nombresPermisos[permiso]}</span>
                  <Form.Check 
                    type="switch"
                    checked={permisosOverride[permiso] || false}
                    onChange={() => handlePermisoChange(permiso, !permisosOverride[permiso])}
                    disabled={!usarPermisosPersonalizados}
                  />
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalPermisos(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleGuardarPermisos} disabled={guardandoPermisos}>
            {guardandoPermisos ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> : '💾 Guardar Permisos'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODAL: SUBIR FIRMA MANUAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal show={showModalFirma} onHide={() => setShowModalFirma(false)} centered>
        <Modal.Header closeButton><Modal.Title>✍️ Cargar Firma Manual</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2" style={{ fontSize: 13 }}>
            💡 Para una experiencia más segura, usa el botón <strong>📧 Solicitar</strong> para que el funcionario registre su firma directamente desde su dispositivo.
          </Alert>
          <input ref={fileInputRef} type="file" className="form-control mb-3" accept="image/*" onChange={handleFileChange} />
          {firmaPreview && <img src={firmaPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', display: 'block', margin: '0 auto' }} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalFirma(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSubirFirma} disabled={!firmaPreview || subiendoFirma}>
            {subiendoFirma ? 'Guardando...' : '💾 Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODAL: VER FIRMA */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal show={showModalVerFirma} onHide={() => setShowModalVerFirma(false)} centered>
        <Modal.Header closeButton><Modal.Title>👁️ Firma de {firmaViendo?.nombres}</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          {firmaViendo?.firma && <img src={firmaViendo.firma} alt="Firma" style={{ maxWidth: '100%', maxHeight: '300px' }} />}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowModalVerFirma(false)}>Cerrar</Button></Modal.Footer>
      </Modal>

      {/* TOAST */}
      {showToast && (
        <div className="toast-container">
          <div className={`toast-notification ${tipoMensaje}`}>
            <button className="toast-close" onClick={ocultarToast}>×</button>
            <div className="toast-header">
              <div className={`toast-icon ${tipoMensaje}`}>
                {tipoMensaje === 'success' && '✓'}
                {tipoMensaje === 'error' && '✕'}
                {tipoMensaje === 'loading' && '⟳'}
              </div>
              <span>{tipoMensaje === 'success' ? 'Éxito' : tipoMensaje === 'error' ? 'Error' : 'Procesando'}</span>
            </div>
            <div className="toast-message">{mensaje}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserManagementPage;